/**
 * 전자계약 거래처 상호명 길이 검증 유틸리티
 *
 * 배경:
 *   modusign 전자서명 API는 metadata value를 최대 80자로 제한한다.
 *   백엔드 inapi는 거래처 정보를 아래와 같이 직렬화하여 client metadata로 전달한다:
 *     JSON.stringify({ name: <거래처 상호>, manager: <대표자명>, mobile: <연락처> })
 *   상호가 길면 이 문자열이 80자를 초과해 계약 생성이 400 에러로 실패한다.
 *
 * 사용법:
 *   import { validateContractClientNameLength } from '@/views/ContractManagement/contracts/contractValidationUtils';
 *   const result = validateContractClientNameLength({
 *     name: clientData.companyName,
 *     manager: clientData.ceo.name,
 *     mobile: clientData.contact.mobile,
 *   });
 *   if (!result.ok) { ... result.message ... }
 */

const MODUSIGN_METADATA_LIMIT = 80;

/**
 * 전자계약 거래처 상호명 길이 제한 검증
 *
 * @param {{ name: string, manager: string, mobile: string }} params
 *   - name    : 거래처 상호
 *   - manager : 대표자명
 *   - mobile  : 거래처 연락처
 * @returns {{ ok: boolean, length: number, limit: number, message: string }}
 *   - ok      : 80자 이하이면 true
 *   - length  : 실제 직렬화 문자열 길이
 *   - limit   : 허용 최대 길이 (80)
 *   - message : 실패 시 사용자 안내 메시지 (한국어)
 *
 * @example
 * // modusign 제한 초과 케이스
 * validateContractClientNameLength({
 *   name: '백장현 치과의원(Dr Paek Dental Clinic)',
 *   manager: '백장현',
 *   mobile: '010-6294-7504',
 * });
 * // → { ok: false, length: 83, limit: 80, message: '...' }
 */
// @MX:ANCHOR: [AUTO] 14개 전자계약 폼에서 공통 호출 (fan_in >= 14). 로직·시그니처 변경 시 모든 폼에 영향.
// @MX:REASON: modusign metadata 80자 제한 규칙의 단일 진실 원천. 이 함수 외부에서 직렬화 길이를 직접 계산하지 않는다.
export function validateContractClientNameLength({ name = '', manager = '', mobile = '' }) {
  // modusign으로 전송되는 client metadata value와 동일한 직렬화 방식
  const value = JSON.stringify({ name, manager, mobile });
  const length = value.length;
  const ok = length <= MODUSIGN_METADATA_LIMIT;

  if (ok) {
    return { ok: true, length, limit: MODUSIGN_METADATA_LIMIT, message: '' };
  }

  // 상호 단독으로 사용 가능한 최대 글자 수 계산
  // name을 빈 문자열로 치환한 기본 JSON 길이를 구하고, 남은 여유 글자 수를 안내한다.
  const baseLength = JSON.stringify({ name: '', manager, mobile }).length;
  const allowedNameLength = Math.max(0, MODUSIGN_METADATA_LIMIT - baseLength);

  const message =
    `거래처 상호가 너무 길어 전자계약을 생성할 수 없습니다. (전자서명 시스템 제한)\n` +
    `현재 거래처 상호를 약 ${allowedNameLength}자 이내로 줄여 등록한 뒤 다시 시도해 주세요.\n` +
    `(예: 영문 별칭 등을 축약하여 거래처 정보를 수정해 주세요.)`;

  return { ok: false, length, limit: MODUSIGN_METADATA_LIMIT, message };
}

/**
 * 상호 뒤에 붙은 영문 별칭 괄호부를 제거한다.
 *
 * 배경:
 *   ERP 원본 상호에 영문 별칭이 괄호로 붙어 상호가 길어지는 케이스가 있다.
 *     예) '백장현 치과의원(Dr Paek Dental Clinic)' → '백장현 치과의원'
 *   dept-batch 가 ERP 원본을 매시 재동기화하므로 DB 를 고쳐도 원복된다.
 *   따라서 계약 생성 시점에 사용자가 상호를 줄이는 것이 유일한 해법이고,
 *   이 함수는 그 후보값을 만들어 "제안"하는 용도다. 자동 적용하지 않는다.
 *
 * 안전장치:
 *   괄호 안이 ASCII(영문·숫자·공백·`.`·`,`·`&`·`-`)로만 구성될 때만 제거한다.
 *   괄호 안에 한글이 있으면 그것이 실제 상호일 수 있으므로 건드리지 않는다.
 *     예) '의료법인장안의료재단은평지점(이가튼튼치과의원)' → 제거 대상 아님
 *   법적 문서에 인쇄되는 값이므로 판단이 애매하면 제거하지 않는 쪽을 택한다.
 *
 * @param {string} name 원본 상호
 * @returns {string} 별칭을 제거한 상호. 제거 대상이 없으면 원본을 그대로 돌려준다.
 */
export function stripAsciiParenthetical(name = '') {
  if (!name) return '';
  // 반각/전각 괄호 모두 대응. 괄호 안이 ASCII 로만 채워진 구간만 제거한다.
  return String(name)
    .replace(/[(（][ A-Za-z0-9.,&-]+[)）]/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}
